import { toClinicDate, clinicNow } from './clinicTime';

// Helpers for "who brought the child in": the Issue Token dialog asks it for every minor, offers
// the adults already on the file and lets the receptionist add one with "Someone else".

// What "Someone else" starts from.
export const EMPTY_NEW_GUARDIAN = { name: '', cnic: '', relationship: '', relationshipOther: '', phoneNumber: '' };

const CNIC_PATTERN = /^\d{5}-\d{7}-\d$/;

// Whole years on the clinic's wall clock, or null without a usable date of birth.
const ageYears = (dateOfBirth) => {
  const birth = toClinicDate(dateOfBirth);
  if (!birth) return null;
  const today = clinicNow();
  let years = today.getFullYear() - birth.getFullYear();
  const months = today.getMonth() - birth.getMonth();
  if (months < 0 || (months === 0 && today.getDate() < birth.getDate())) years--;
  return years;
};

// A patient the dialog must ask about. The backend's own verdict wins when it is there; a row
// from an older list without it falls back to the date of birth.
export const isMinorPatient = (patient) => {
  if (!patient) return false;
  if (typeof patient.adult === 'boolean') return !patient.adult;
  if (patient.minor === true) return true;
  const years = ageYears(patient.dateOfBirth);
  return years !== null && years < 18;
};

// The first thing wrong with a new guardian, as a message for the receptionist, or null.
export const newGuardianProblem = (g) => {
  if (!g || !g.name || !g.name.trim()) return 'Please enter the name of the adult who brought the child';
  if (!g.relationship) return 'Please pick how this adult is related to the child';
  if (g.relationship === 'OTHER' && !(g.relationshipOther || '').trim()) return 'Please say what the relationship is';
  if (g.cnic && !CNIC_PATTERN.test(g.cnic)) return 'CNIC must be 13 digits, like 42401-1234567-8';
  return null;
};

// The new guardian as the backend takes it: trimmed, blanks as null, the free-text relationship
// only when it applies.
export const newGuardianPayload = (g) => ({
  name: g.name.trim(),
  cnic: g.cnic ? g.cnic.trim() : null,
  relationship: g.relationship || null,
  relationshipOther: g.relationship === 'OTHER' ? (g.relationshipOther || '').trim() || null : null,
  phoneNumber: (g.phoneNumber || '').trim() || null,
});

// A child's own parents, offered in the Issue Token dialog as choices that need nothing typed:
// their names, and a CNIC when the file has one, are already on the record. Registration stopped
// asking who brings the child (2026-09-23), so without these a receptionist would have to retype
// the father's details on every first token.
export const PARENT_OPTION_PREFIX = 'PARENT:';

/**
 * The parent options to offer for `patient`, given the adults already on their file. A parent is
 * skipped when an existing guardian row already covers them — by relationship, or by the same CNIC
 * under any relationship — so picking one can never ask the backend to add a duplicate (which it
 * refuses with "This guardian is already on the child's file").
 */
export const parentOptionsFor = (patient, guardians = []) => {
  if (!patient) return [];
  const rows = Array.isArray(guardians) ? guardians : [];
  const takenRelationships = new Set(rows.map((g) => g.relationship).filter(Boolean));
  const takenCnics = new Set(rows.map((g) => (g.cnic || '').trim()).filter(Boolean));

  const options = [];
  const offer = (relationship, rawName, rawCnic) => {
    const name = (rawName || '').trim();
    if (!name || takenRelationships.has(relationship)) return;
    const cnic = (rawCnic || '').trim() || null;
    if (cnic && takenCnics.has(cnic)) return;
    options.push({ value: `${PARENT_OPTION_PREFIX}${relationship}`, relationship, name, cnic });
  };
  offer('FATHER', patient.fatherName, patient.fatherCnic);
  offer('MOTHER', patient.motherName, patient.motherCnic);
  return options;
};

/** True for a value produced by parentOptionsFor. */
export const isParentOption = (value) =>
  typeof value === 'string' && value.startsWith(PARENT_OPTION_PREFIX);

/** A parent option as the backend takes a new guardian. */
export const parentGuardianPayload = (option) => ({
  name: option.name,
  cnic: option.cnic || null,
  relationship: option.relationship,
  relationshipOther: null,
  phoneNumber: null,
});

// The adult a visit records as having brought the child, in the shape describeGuardian reads, or
// null for an adult's visit. Works on a Visit and on the token-creation response alike.
export const accompaniedByOf = (visit) => {
  if (!visit || !visit.accompaniedByName) return null;
  return {
    id: visit.accompaniedByGuardianId || null,
    name: visit.accompaniedByName,
    relationship: visit.accompaniedByRelationship || null,
    relationshipOther: visit.accompaniedByRelationshipOther || null,
  };
};
